/**
 * ProjectSustainabilityForm.jsx — Proje Başına Sürdürülebilirlik Formu
 * Route: /Management/Projects/:id/sustainability (staff, page.proje)
 *        /Firma/Projeler/:id/surdurulebilirlik (company, PROJELER; #712 -- the Proje Beyanı Formlar step)
 *
 * KURAL (Kural 3): saveProjectSustainability çağrılmadan önce
 *   answers dizisi temizlenir — her obje sadece o sorunun tipine ait tek field içerir.
 * NOT: generalInfo read-only'dir, kullanıcıya düzenletme.
 * NOT: standSize null gelebilir (kroki sistemi rebuild'de) → UI'da "—" göster.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader,
  Row, Col, Button, Badge, Input, FormGroup, Label,
  Alert, Spinner,
} from "reactstrap";
import { ArrowLeft, Save, Leaf, RefreshCw } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { getProjectSustainability, saveProjectSustainability } from "../../../services/projectService";
import { geriHedefiOku } from "../../../Components/Kisi/kisiBaglantilari";
import { useTranslation } from "react-i18next";

// ─── Tip → Field eşlemesi ────────────────────────────────────────────────────
const TYPE_FIELD = {
  NUMBER:        'number',
  BOOLEAN:       'bool',
  SINGLE_CHOICE: 'choice',
  MULTI_CHOICE:  'choices',
  TEXT:          'text',
};

// ─── Kural 3: Payload temizleyici ─────────────────────────────────────────────
function cleanAnswers(rawAnswers, definition) {
  const allQuestions = (definition?.sections ?? []).flatMap(s => s.questions ?? []);
  return rawAnswers
    .map(a => {
      const q = allQuestions.find(q => q.key === a.questionKey);
      if (!q) return null;
      const field = TYPE_FIELD[q.type];
      const val = a[field];
      if (val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) return null;
      return { questionKey: a.questionKey, [field]: val };
    })
    .filter(Boolean);
}

// ─── Soru bileşenleri ─────────────────────────────────────────────────────────
function QuestionInput({ question, value, onChange }) {
  const { t } = useTranslation("portal");
  switch (question.type) {
    case 'NUMBER':
      return (
        <Input
          type="number"
          bsSize="sm"
          style={{ maxWidth: 160 }}
          value={value ?? ''}
          onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
          placeholder={question.unit ? t("surdurulebilirlik.sayi", { unit: question.unit }) : t("surdurulebilirlik.sayi2")}
        />
      );
    case 'BOOLEAN':
      return (
        <div className="d-flex gap-3">
          {[{ v: true, label: t("common:evet") }, { v: false, label: t("common:hayir") }].map(opt => (
            <Label key={String(opt.v)} check className="d-flex align-items-center gap-1 cursor-pointer mb-0">
              <Input
                type="radio"
                name={question.key}
                checked={value === opt.v}
                onChange={() => onChange(opt.v)}
              />
              <Badge color={opt.v ? 'success' : 'secondary'}>{opt.label}</Badge>
            </Label>
          ))}
          {value !== null && value !== undefined && (
            <Button color="link" size="sm" className="p-0 text-muted" onClick={() => onChange(null)}>
              {t("surdurulebilirlik.temizle")}
            </Button>
          )}
        </div>
      );
    case 'SINGLE_CHOICE':
      return (
        <Input
          type="select"
          bsSize="sm"
          style={{ maxWidth: 280 }}
          value={value ?? ''}
          onChange={e => onChange(e.target.value || null)}
        >
          <option value="">{t("surdurulebilirlik.secin")}</option>
          {(question.choices || []).map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Input>
      );
    case 'MULTI_CHOICE':
      return (
        <div className="d-flex flex-wrap gap-2">
          {(question.choices || []).map(c => {
            const selected = (value || []).includes(c);
            return (
              <Button
                key={c}
                color={selected ? 'primary' : 'secondary'}
                outline={!selected}
                size="sm"
                onClick={() => {
                  const curr = value || [];
                  onChange(selected ? curr.filter(x => x !== c) : [...curr, c]);
                }}
              >
                {c}
              </Button>
            );
          })}
        </div>
      );
    case 'TEXT':
      return (
        <Input
          type="textarea"
          bsSize="sm"
          rows={3}
          value={value ?? ''}
          onChange={e => onChange(e.target.value || null)}
          placeholder={t("surdurulebilirlik.notunuzuYazin")}
        />
      );
    default:
      return <span className="text-muted small">{t("surdurulebilirlik.bilinmeyenSoruTipi")}{" "}{question.type}</span>;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
export default function ProjectSustainabilityForm() {
  const { t } = useTranslation("portal");
  const { id } = useParams();
  const navigate = useNavigate();
  // #712: only the `mock-...` projects of projectService live in memory; any other id is a real project and goes to the backend
  // (WAS: the service's USE_MOCK_DATA flag decided, so a real project showed the mock questions and its answers were never saved).
  const canliProje = !String(id).startsWith("mock-");
  // #712: the company's Proje Beyanı opens this page in a new tab, where navigate(-1) has nowhere to go: ?geri= / ?geriEtiket= name the way back.
  const [searchParams] = useSearchParams();
  const geriHedefi = geriHedefiOku(searchParams);
  const geriGit = () => (geriHedefi ? navigate(geriHedefi.from) : navigate(-1));
  const geriEtiketi = geriHedefi?.fromLabel ?? t("common:geri");

  const [payload, setPayload] = useState(null);  // { projectId, generalInfo, definition, answers }
  const [answers, setAnswers] = useState({});     // { questionKey: value }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  // ─── Veri yükle ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProjectSustainability(id, { forceLive: canliProje });
      setPayload(data);
      // Mevcut cevapları state'e çevir: [{ questionKey, number/bool/... }] → { key: value }
      const init = {};
      (data.answers || []).forEach(a => {
        const field = Object.values(TYPE_FIELD).find(f => a[f] !== null && a[f] !== undefined);
        if (field) init[a.questionKey] = a[field];
      });
      setAnswers(init);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, canliProje]);

  useEffect(() => {
    (async () => { await load(); })();
  }, [load]);

  // ─── Cevap güncelle ─────────────────────────────────────────────────────
  function setAnswer(questionKey, value) {
    setAnswers(prev => ({ ...prev, [questionKey]: value }));
    setSaved(false);
  }

  // ─── Kaydet ─────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!payload?.definition) return;
    setSaving(true);
    setError(null);
    try {
      // Kural 3: answers state'ini temizlenmiş dizi formatına çevir
      const rawAnswers = Object.entries(answers).map(([questionKey, value]) => {
        const allQuestions = (payload.definition.sections || []).flatMap(s => s.questions || []);
        const q = allQuestions.find(q => q.key === questionKey);
        if (!q) return null;
        const field = TYPE_FIELD[q.type];
        return { questionKey, [field]: value };
      }).filter(Boolean);

      const cleaned = cleanAnswers(rawAnswers, payload.definition);
      await saveProjectSustainability(id, cleaned, { forceLive: canliProje });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="page-content">
      <Container fluid className="text-center py-5"><Spinner color="primary" /></Container>
    </div>
  );

  if (error && !payload) return (
    <div className="page-content">
      <Container fluid>
        <Alert color="danger">{error}</Alert>
        <Button color="secondary" onClick={geriGit}>
          <ArrowLeft size={14} className="me-1" />{" "}{geriEtiketi}
        </Button>
      </Container>
    </div>
  );

  const { generalInfo, definition } = payload || {};

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("surdurulebilirlik.surdurulebilirlikFormu")} pageTitle={t("surdurulebilirlik.projeler")} />

        {saved && <Alert color="success">{t("surdurulebilirlik.cevaplarKaydedildi")}</Alert>}
        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

        {/* ─── Başlık Bar ─── */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Button color="secondary" outline size="sm" onClick={geriGit}>
            <ArrowLeft size={14} className="me-1" />{" "}{geriEtiketi}
          </Button>
          <div className="d-flex gap-2">
            <Button color="info" outline size="sm" onClick={load} title={t("common:yenile")}>
              <RefreshCw size={14} />
            </Button>
            <Button color="success" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <Save size={14} className="me-1" />}
              {t("common:kaydet")}
            </Button>
          </div>
        </div>

        {/* ─── Genel Bilgiler (read-only) ─── */}
        <Card className="mb-3 border-success">
          <CardHeader className="bg-success bg-opacity-10">
            <Leaf size={15} className="me-2 text-success" />
            <span className="fw-semibold text-success">{definition?.name ?? t("surdurulebilirlik.surdurulebilirlikFormu")}</span>
            <Badge color="secondary" className="ms-2 small">v{definition?.version ?? 1}</Badge>
          </CardHeader>
          <CardBody>
            <Row className="g-3">
              {[
                { label: t("surdurulebilirlik.katilimciFirma"),  value: generalInfo?.exhibitorName },
                { label: t("surdurulebilirlik.dekorator"),        value: generalInfo?.contractorName },
                { label: t("surdurulebilirlik.standNo"),         value: generalInfo?.standNumber },
                { label: t("surdurulebilirlik.standBoyutu"),     value: generalInfo?.standSize ?? '—' },
              ].map(item => (
                <Col key={item.label} md={3}>
                  <div className="text-muted small">{item.label}</div>
                  <div className="fw-semibold">{item.value || '—'}</div>
                </Col>
              ))}
            </Row>
            <div className="text-muted small mt-2">
              {t("surdurulebilirlik.genelBilgilerOtomatikDoldurulurDuzenlenemez")}
            </div>
          </CardBody>
        </Card>

        {/* ─── Soru Bölümleri ─── */}
        {(definition?.sections ?? [])
          .sort((a, b) => a.order - b.order)
          .map(section => (
            <Card key={section.key} className="mb-3">
              <CardHeader>
                <h6 className="mb-0">{section.title}</h6>
              </CardHeader>
              <CardBody>
                {(section.questions ?? [])
                  .sort((a, b) => a.order - b.order)
                  .map(question => (
                    <FormGroup key={question.key} className="mb-4">
                      <Label className="fw-semibold mb-2">
                        {question.order}. {question.label}
                        {question.unit && (
                          <Badge color="light" className="ms-2 text-muted border small">
                            {question.unit}
                          </Badge>
                        )}
                        <Badge color="light" className="ms-1 text-muted border" style={{ fontSize: '10px' }}>
                          {question.type}
                        </Badge>
                      </Label>
                      <div>
                        <QuestionInput
                          question={question}
                          value={answers[question.key] ?? null}
                          onChange={val => setAnswer(question.key, val)}
                        />
                      </div>
                    </FormGroup>
                  ))}
              </CardBody>
            </Card>
          ))}

        {/* ─── Alt Kaydet Butonu ─── */}
        <div className="d-flex justify-content-end mb-4">
          <Button color="success" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-1" /> : <Save size={14} className="me-1" />}
            {t("surdurulebilirlik.cevaplariKaydet")}
          </Button>
        </div>
      </Container>
    </div>
  );
}

